import { useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { resetPassword } from '@/api/auth'
import toast from 'react-hot-toast'

export default function ResetPasswordPage() {
  const navigate = useNavigate()
  const { token } = useParams<{ token: string }>()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!password || !confirmPassword) {
      toast.error('Please fill in all fields')
      return
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match')
      return
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters')
      return
    }
    if (!token) {
      toast.error('Invalid or missing reset token')
      return
    }

    setLoading(true)
    try {
      await resetPassword(token, { password, confirm_password: confirmPassword })
      setSuccess(true)
      toast.success('Password updated successfully!')
      setTimeout(() => navigate('/', { replace: true }), 2500)
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to reset password'
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: 'var(--color-bg)',
      padding: '2rem',
    }}>
      <div style={{
        maxWidth: 420,
        width: '100%',
        animation: 'slideUp 0.3s ease-out',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <span style={{
            fontFamily: 'var(--font-heading)',
            fontSize: '1.8125rem',
            fontWeight: 700,
            background: 'linear-gradient(90deg, #0061D0, #009974)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            twasta.ai
          </span>
        </div>

        <div style={{
          background: 'var(--color-bg-secondary)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          padding: '2rem',
        }}>
          {success ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                background: 'var(--color-accent-weak)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.25rem',
              }}>
                <span className="material-icons" style={{ fontSize: 29, color: 'var(--color-accent)' }}>
                  check_circle
                </span>
              </div>
              <h2 style={{ fontSize: '1.3125rem', fontWeight: 600, marginBottom: '0.75rem' }}>
                Password Updated
              </h2>
              <p style={{
                color: 'var(--color-text-secondary)',
                fontSize: '0.9375rem',
                lineHeight: 1.7,
                marginBottom: '1.5rem',
              }}>
                Your password has been reset successfully. Redirecting you to login...
              </p>
              <Link to="/" className="btn btn-primary">
                Go to Login
              </Link>
            </div>
          ) : (
            <>
              <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                <span className="material-icons" style={{
                  fontSize: 41,
                  color: 'var(--color-accent)',
                  marginBottom: '0.75rem',
                  display: 'block',
                }}>
                  lock
                </span>
                <h2 style={{ fontSize: '1.3125rem', fontWeight: 600, marginBottom: '0.375rem' }}>
                  Set New Password
                </h2>
                <p style={{
                  color: 'var(--color-text-secondary)',
                  fontSize: '0.9375rem',
                }}>
                  Choose a strong password for your account.
                </p>
              </div>

              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                <div>
                  <label htmlFor="new-password">New Password</label>
                  <input
                    id="new-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Minimum 8 characters"
                    autoComplete="new-password"
                    required
                    autoFocus
                  />
                </div>

                <div>
                  <label htmlFor="confirm-new-password">Confirm New Password</label>
                  <input
                    id="confirm-new-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter new password"
                    autoComplete="new-password"
                    required
                  />
                </div>

                {/* Password strength indicator */}
                {password && (
                  <div style={{ display: 'flex', gap: 4 }}>
                    {[1, 2, 3, 4].map((level) => {
                      const strength = password.length >= 12 ? 4 : password.length >= 10 ? 3 : password.length >= 8 ? 2 : 1
                      const colors = ['var(--color-error)', '#ff9800', '#ffeb3b', 'var(--color-accent)']
                      return (
                        <div
                          key={level}
                          style={{
                            flex: 1,
                            height: 3,
                            borderRadius: 2,
                            background: level <= strength ? colors[strength - 1] : 'var(--color-border)',
                            transition: 'background 0.2s',
                          }}
                        />
                      )
                    })}
                  </div>
                )}

                <button
                  type="submit"
                  className="btn btn-primary btn-lg w-full"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} />
                      Updating...
                    </>
                  ) : (
                    'Update Password'
                  )}
                </button>
              </form>
            </>
          )}
        </div>

        <div style={{
          textAlign: 'center',
          marginTop: '1.25rem',
          fontSize: '0.9375rem',
          color: 'var(--color-text-secondary)',
        }}>
          <Link to="/">Back to Login</Link>
        </div>
      </div>
    </div>
  )
}
